import { useEffect, useState } from "react";
import { Sparkle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useFetch } from "@/lib/api";
import { PageHeader, Panel } from "@/components/common";
import { NewLaunch } from "@/pages/Launchpad";
import { CampaignCard, ReplyCard, FixCard, MeetingCard, Section } from "@/components/OneCards";

const APP = "https://masa.platform.specia.ai";
export const MASA_PROMO = {
  name: "Meet MaSa - early access",
  image: "/promo/masa-hero.jpg",
  text: `MaSa is an AI agent from Specia AI that works as a complete sales and marketing team for Australian B2B businesses. Voice: MaSa speaks for itself in the first person - confident and a little witty, polished and professional, with a bold, futuristic edge ("G'day, I'm MaSa. I run the follow-ups you never get to.").

What MaSa does: researches prospects that fit and shows the source for every fact; writes personal emails, newsletters and LinkedIn/X posts in the business's voice; builds carousels and short videos from one image; sends at a safe warm-up pace and chases every follow-up on day 3 and day 7; reads replies, handles out-of-office and referrals, removes dead addresses automatically; books interested prospects straight into the calendar. The owner just approves - one approval covers a whole campaign - and every exception comes with a plain-English fix guide.

Trust: follows the Australian Spam Act, honours every unsubscribe instantly, never sends without approval.

Offer: pricing is not announced yet. The only call to action is to join the free early-access waitlist at ${APP}/waitlist - early-access spots open in small batches. Do not mention prices or discounts.`,
};

const STATS = [["sent", "Emails sent"], ["delivered", "Delivered"], ["replies", "Replies"], ["meetings", "Meetings"], ["opt_outs", "Opt-outs"], ["removed", "Dead addresses removed"]];

const Results = ({ r }) => (
  <div className="grid grid-cols-3 md:grid-cols-6 gap-px bg-border rounded-md overflow-hidden" data-testid="one-results">
    {STATS.map(([k, l]) => (
      <div key={k} className="bg-[#0A0A0A] p-4" data-testid={`one-stat-${k}`}>
        <div className="text-2xl font-semibold text-white tabular-nums">{(r?.[k] ?? 0).toLocaleString()}</div>
        <div className="text-xs text-zinc-500 mt-1">{l}</div>
      </div>
    ))}
  </div>
);

export default function OnePlace() {
  const { data, reload } = useFetch("/oneplace");
  const { data: past } = useFetch("/launches");
  const [preset, setPreset] = useState(null);
  const building = data?.campaigns?.some((c) => c.status === "building");
  useEffect(() => {
    if (!building) return undefined;
    const t = setInterval(reload, 8000);
    return () => clearInterval(t);
  }, [building, reload]);
  const total = data ? data.campaigns.length + data.replies.length + data.fixes.length : 0;
  return (
    <div className="space-y-6" data-testid="oneplace-page">
      <PageHeader eyebrow="MaSa One" title="Drop it in. MaSa does the rest." desc="Add the offering, an image and a contact list. Agents build, send, follow up and handle bounces and referrals. Anything that needs you shows up under Waiting for you.">
        <Button data-testid="one-masa-promo-btn" onClick={() => setPreset({ ...MASA_PROMO })} className="gap-2 bg-gradient-to-r from-[#6D28D9] via-[#C026D3] to-[#F97316] text-white hover:opacity-90"><Sparkle weight="fill" /> Promote MaSa itself</Button>
      </PageHeader>
      <Results r={data?.results} />
      <div className="grid xl:grid-cols-[1fr_440px] gap-6 items-start">
        <NewLaunch past={past} onCreated={reload} preset={preset} />
        <Panel title={`Waiting for you${total ? ` (${total})` : ""}`} data-testid="one-inbox">
          {!data ? <p className="text-xs text-zinc-500">Loading…</p> : (
            <div className="space-y-6">
              <Section testid="one-section-campaigns" title="New campaigns" items={data.campaigns} empty="Nothing to approve.">
                {data.campaigns.map((c) => <CampaignCard key={c.id} c={c} onDone={reload} />)}
              </Section>
              <Section testid="one-section-replies" title="Hot leads & replies" items={data.replies} empty="No replies need an answer.">
                {data.replies.map((e) => <ReplyCard key={e.id} e={e} onDone={reload} />)}
              </Section>
              <Section testid="one-section-fixes" title="Admin fixes" items={data.fixes} empty="Nothing needs an admin.">
                {data.fixes.map((e) => <FixCard key={e.id} e={e} onDone={reload} />)}
              </Section>
              <Section testid="one-section-meetings" title="Upcoming meetings" items={data.meetings} empty="No meetings booked yet.">
                {data.meetings.map((x) => <MeetingCard key={x.id} x={x} />)}
              </Section>
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
